
import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const form = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d64ut1i5k2n47qsg6v1g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30"></div>
        
        <div className="relative max-w-[1400px] mx-auto px-8 py-20 w-full">
          <div className="flex items-center justify-between gap-16">
            {/* Left Content */}
            <div className="flex-1">
              <div className="mb-12">
                <img 
                  src="https://public.readdy.ai/ai/img_res/1ebd43be-c658-4a27-ba71-8c4e13ff0694.png" 
                  alt="UNIK SH.P.K. Logo" 
                  className="h-16 w-auto object-contain"
                />
              </div>
              
              <h1 className="text-7xl font-bold mb-8 leading-tight text-slate-900">
                Crafting Premium<br />
                iOS Experiences<br />
                for Modern Users
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
                A team of experts developing modern applications for the iOS platform, designed to enhance user experience and improve mobile device security.
              </p>
            </div>

            {/* Right Image */}
            <div className="flex-1 flex justify-center">
              <div className="relative">
                <img 
                  src="https://public.readdy.ai/ai/img_res/c0681cb4e93a307d38529e5b5db0a375.jpg" 
                  alt="VPN Application Interface" 
                  className="w-full h-auto object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="flex items-center gap-20">
            {/* Left Image */}
            <div className="flex-1">
              <div className="rounded-[40px] overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%20modern%20technology%20background%20with%20iOS%20interface%20elements%20floating%20geometric%20shapes%20gradient%20mesh%20in%20soft%20purple%20and%20blue%20tones%20digital%20innovation%20concept%20clean%20minimalist%20design%20professional%20tech%20aesthetic&width=700&height=700&seq=about001&orientation=squarish" 
                  alt="Technology Innovation" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right Content */}
            <div className="flex-1">
              <div className="inline-block px-5 py-2 bg-indigo-50 text-indigo-600 rounded-full text-sm font-semibold mb-8">
                ABOUT US
              </div>
              
              <h2 className="text-6xl font-bold mb-10 leading-tight text-slate-900">
                Expert Team Delivering<br />
                Excellence Since Day One
              </h2>
              
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                At UNIK SH.P.K., we are dedicated to creating innovative iOS applications that prioritize <em className="text-slate-700">user experience</em> and <em className="text-slate-700">mobile security</em>. Our team combines technical expertise with creative vision to deliver products that make a real difference.
              </p>
              
              <p className="text-lg text-slate-600 leading-relaxed">
                From VPN solutions that protect your privacy to cleaning tools that optimize performance, every application we develop reflects our commitment to quality and innovation in the iOS ecosystem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-8">
          <h2 className="text-7xl font-bold text-center mb-20 text-slate-900 font-serif">
            Our Flagship<br />
            Products
          </h2>

          {/* VPN Product Card */}
          <div className="bg-white rounded-[32px] border border-slate-200 p-16 mb-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-16">
              <div className="flex-[6]">
                <img 
                  src="https://readdy.ai/api/search-image?query=iPhone%20displaying%20VPN%20application%20interface%20with%20security%20features%20encryption%20shield%20icon%20server%20locations%20map%20connection%20speed%20indicator%20modern%20UI%20design%20clean%20white%20background%20professional%20product%20shot%20natural%20shadow&width=800&height=600&seq=vpn001&orientation=landscape" 
                  alt="VPN Application" 
                  className="w-full h-auto object-contain"
                />
              </div>
              
              <div className="flex-[4]">
                <h3 className="text-4xl font-bold mb-6 text-slate-900 font-serif">VPN Application</h3>
                <p className="text-lg text-slate-600 leading-relaxed mb-4">
                  Our VPN application provides military-grade encryption to protect your online privacy and security. With servers located worldwide, you can access content from anywhere while maintaining complete anonymity.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Features include automatic connection, kill switch protection, lightning-fast speeds, and an intuitive interface designed specifically for iOS devices.
                </p>
              </div>
            </div>
          </div>

          {/* Cleaner Product Card */}
          <div className="bg-white rounded-[32px] border border-slate-200 p-16 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-16">
              <div className="flex-[4]">
                <h3 className="text-4xl font-bold mb-6 text-slate-900 font-serif">Cleaner Application</h3>
                <p className="text-lg text-slate-600 leading-relaxed mb-4">
                  Keep your iOS device running at peak performance with our intelligent Cleaner application. It identifies and removes unnecessary files, optimizes storage, and enhances overall system efficiency.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Advanced algorithms detect duplicate photos, large files, and cached data, helping you reclaim valuable storage space while maintaining your device's speed and responsiveness.
                </p>
              </div>
              
              <div className="flex-[6]">
                <img 
                  src="https://readdy.ai/api/search-image?query=iPhone%20showing%20cleaner%20application%20interface%20with%20storage%20optimization%20features%20file%20management%20system%20performance%20graphs%20duplicate%20photo%20detection%20modern%20clean%20UI%20white%20background%20professional%20product%20photography&width=800&height=600&seq=cleaner001&orientation=landscape" 
                  alt="Cleaner Application" 
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[2px] text-slate-400 mb-4">OUR EXPERTISE</p>
            <h2 className="text-7xl font-extrabold text-slate-900 leading-tight">
              Innovative Solutions<br />
              Built for iOS
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-3xl p-10 shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-shield-line text-5xl text-slate-900"></i>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-slate-900">VPN Technology</h3>
              <p className="text-base text-slate-600 leading-relaxed">
                Advanced encryption protocols ensure your data remains secure and private. Our VPN technology creates a protected tunnel for all your internet traffic, safeguarding you from threats and surveillance.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-3xl p-10 shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-5xl text-slate-900"></i>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-slate-900">Performance Optimization</h3>
              <p className="text-base text-slate-600 leading-relaxed">
                Our Cleaner app intelligently analyzes your device to identify performance bottlenecks. It removes junk files, optimizes memory usage, and ensures your iOS device runs smoothly and efficiently.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-3xl p-10 shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-5xl text-slate-900"></i>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-slate-900">Privacy First</h3>
              <p className="text-base text-slate-600 leading-relaxed">
                We believe privacy is a fundamental right. All our applications are designed with privacy at their core, ensuring your personal data never leaves your device without your explicit consent.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="flex gap-20">
            {/* Left - Contact Info */}
            <div className="flex-1">
              <h2 className="text-6xl font-bold mb-12 text-slate-900">Get in Touch</h2>
              
              <div className="space-y-8">
                <div>
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-6 h-6 flex items-center justify-center mt-1">
                      <i className="ri-map-pin-line text-xl text-slate-700"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-500 mb-1">Address</p>
                      <p className="text-base text-slate-900">Rruga Ibrahim Rrugova, pallati Sky Tower, kati 10, zyra 10/2</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-6 h-6 flex items-center justify-center mt-1">
                      <i className="ri-building-line text-xl text-slate-700"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-500 mb-1">City</p>
                      <p className="text-base text-slate-900">TIRANE</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-6 h-6 flex items-center justify-center mt-1">
                      <i className="ri-mail-line text-xl text-slate-700"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-500 mb-1">Zip</p>
                      <p className="text-base text-slate-900">1000</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-6 h-6 flex items-center justify-center mt-1">
                      <i className="ri-global-line text-xl text-slate-700"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-500 mb-1">Country</p>
                      <p className="text-base text-slate-900">Albania</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-6 h-6 flex items-center justify-center mt-1">
                      <i className="ri-mail-send-line text-xl text-slate-700"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-500 mb-1">Email</p>
                      <p className="text-base text-slate-900">information@unikshpk.com</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-6 h-6 flex items-center justify-center mt-1">
                      <i className="ri-phone-line text-xl text-slate-700"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-500 mb-1">Number</p>
                      <p className="text-base text-slate-900">+1 7314624319</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Contact Form */}
            <div className="flex-1">
              <div className="bg-slate-50 rounded-3xl p-12">
                <h3 className="text-3xl font-bold mb-8 text-slate-900">Send us a Message</h3>
                
                <form id="contact_form" onSubmit={handleSubmit} data-readdy-form>
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 transition-all resize-none"
                      ></textarea>
                      <p className="text-xs text-slate-500 mt-1">{formData.message.length}/500 characters</p>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-8 py-4 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                    </button>

                    {formStatus === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                        Message sent successfully! We'll get back to you soon.
                      </div>
                    )}

                    {formStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                        Failed to send message. Please try again.
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-20 rounded-t-[32px] mx-8">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-4 gap-12 mb-16">
            {/* Column 1 - Company */}
            <div>
              <h3 className="text-4xl font-bold mb-4">UNIK SH.P.K.</h3>
              <p className="text-slate-400 text-base">Premium iOS Development</p>
            </div>

            {/* Column 2 - Products */}
            <div>
              <h4 className="text-sm uppercase tracking-wider text-slate-500 mb-6">PRODUCTS</h4>
              <ul className="space-y-3">
                <li className="text-slate-300 text-base cursor-pointer hover:text-white transition-colors">VPN Application</li>
                <li className="text-slate-300 text-base cursor-pointer hover:text-white transition-colors">Cleaner Tool</li>
                <li className="text-slate-300 text-base cursor-pointer hover:text-white transition-colors">iOS Solutions</li>
                <li className="text-slate-300 text-base cursor-pointer hover:text-white transition-colors">Security Features</li>
                <li className="text-slate-300 text-base cursor-pointer hover:text-white transition-colors">Performance Tools</li>
              </ul>
            </div>

            {/* Column 3 - Connect */}
            <div>
              <h4 className="text-sm uppercase tracking-wider text-slate-500 mb-6">CONNECT</h4>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-slate-300 text-base cursor-pointer hover:text-white transition-colors">
                  <i className="ri-instagram-line text-xl"></i>
                  <span>Instagram</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300 text-base cursor-pointer hover:text-white transition-colors">
                  <i className="ri-linkedin-line text-xl"></i>
                  <span>LinkedIn</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300 text-base cursor-pointer hover:text-white transition-colors">
                  <i className="ri-github-line text-xl"></i>
                  <span>GitHub</span>
                </li>
              </ul>
            </div>

            {/* Column 4 - Contact */}
            <div>
              <h4 className="text-sm uppercase tracking-wider text-slate-500 mb-6">CONTACT INFORMATION</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <span className="text-slate-500">Address:</span>
                  <span className="text-slate-200 ml-2">Rruga Ibrahim Rrugova, pallati Sky Tower, kati 10, zyra 10/2</span>
                </li>
                <li>
                  <span className="text-slate-500">City:</span>
                  <span className="text-slate-200 ml-2">TIRANE</span>
                </li>
                <li>
                  <span className="text-slate-500">Zip:</span>
                  <span className="text-slate-200 ml-2">1000</span>
                </li>
                <li>
                  <span className="text-slate-500">Country:</span>
                  <span className="text-slate-200 ml-2">Albania</span>
                </li>
                <li>
                  <span className="text-slate-500">Email:</span>
                  <span className="text-slate-200 ml-2 font-medium">information@unikshpk.com</span>
                </li>
                <li>
                  <span className="text-slate-500">Phone:</span>
                  <span className="text-slate-200 ml-2 font-medium">+1 7314624319</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="pt-8 border-t border-slate-800 flex justify-between items-center">
            <p className="text-slate-500 text-sm">© 2025 UNIK SH.P.K. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-slate-500 text-sm hover:text-white transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>

        {/* Decorative Background Text */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none">
          <div className="text-[180px] font-black text-white/[0.03] text-center">UNIK</div>
        </div>
      </footer>
    </div>
  );
}
